import React, { useCallback, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import Navbar from '../../../../ReusableBlocks/Navbar';
import LeadershipMetricRow from '../Components/shared/LeadershipMetricRow';
import LeadershipMetricPanelHeader from '../Components/shared/LeadershipMetricPanelHeader';
import LeadershipCompanyStackedChart from '../Components/shared/LeadershipCompanyStackedChart';
import { PARTNER_STORAGE_KEYS } from '../Constants/partnerStorageKeys';
import { PARTNER_PORTAL_NOT_AUTHORIZED, buildPaeQueueProjectUrl } from '../Constants/partnerRoutes';
import { useLeadershipPipeline } from '../Hooks/useLeadershipPipeline';
import { usePartnerRoleAccess } from '../Hooks/usePartnerRoleAccess';
import PartnerLayout from '../Layout/PartnerLayout';
import PageHeader from '../Components/shared/PageHeader';
import { partnerStorage } from '../Storage/partnerStorage';
import { formatPartnerDate } from '../Utils/partnerDateUtils';
import {
  buildLeadershipPaeQueueUrl,
  buildLeadershipPaeQueueUrlFromKpi,
  LEADERSHIP_KPI_FILTERS,
} from '../Utils/partnerLeadershipQueueUtils';

const PRODUCT_COLORS = ['#2563eb', '#0f766e', '#7c3aed', '#0d9488', '#c2410c', '#64748b'];
const ENGAGEMENT_COLORS = ['#0d9488', '#2563eb', '#7c3aed', '#c2410c', '#ca8a04', '#64748b'];
const REGION_LABELS = {
  LATAM: 'LATAM',
  EMEA: 'EMEA',
  APJ: 'APJ',
  NA: 'NA',
  US: 'North America',
};

const readLeadershipDarkMode = () => (
  partnerStorage.get(PARTNER_STORAGE_KEYS.LEADERSHIP_DARK_MODE, false) === true
);

const LeadershipDashboardPage = () => {
  const access = usePartnerRoleAccess();
  const navigate = useNavigate();
  const { pipeline, loading, error } = useLeadershipPipeline(access.canAccessLeadershipPages);
  const [isDarkMode, setIsDarkMode] = useState(readLeadershipDarkMode);

  const handleDarkModeToggle = useCallback(() => {
    setIsDarkMode((current) => {
      const next = !current;
      partnerStorage.set(PARTNER_STORAGE_KEYS.LEADERSHIP_DARK_MODE, next);
      return next;
    });
  }, []);

  const pageClassName = [
    'leadership-dashboard',
    'partner-leadership-page',
    isDarkMode ? 'is-dark' : '',
  ].filter(Boolean).join(' ');

  const darkModeControl = (
    <button
      type="button"
      className="leadership-dark-mode-toggle"
      onClick={handleDarkModeToggle}
      aria-pressed={isDarkMode}
      title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      <span className="leadership-dark-mode-toggle-icon" aria-hidden="true">◐</span>
      <span className="leadership-dark-mode-toggle-label">
        {isDarkMode ? 'Light mode' : 'Dark mode'}
      </span>
    </button>
  );

  const metrics = pipeline.statCards;

  const productsByCount = useMemo(
    () => [...pipeline.byProduct].sort((left, right) => right.count - left.count),
    [pipeline.byProduct],
  );

  const engagementByCount = useMemo(
    () => [...pipeline.byEngagementType].sort((left, right) => right.count - left.count),
    [pipeline.byEngagementType],
  );

  const regionByCount = useMemo(
    () => [...pipeline.byRegion].sort((left, right) => right.count - left.count),
    [pipeline.byRegion],
  );

  const completedRegionQuarterRows = useMemo(
    () => [...pipeline.byRegionCompletedQuarter]
      .map((row) => ({
        ...row,
        displayName: REGION_LABELS[row.region] || row.region,
        companyName: row.region,
      }))
      .sort((left, right) => right.total - left.total),
    [pipeline.byRegionCompletedQuarter],
  );

  const companyStatusRows = useMemo(
    () => [...pipeline.byCompanyStatus].sort((left, right) => right.total - left.total),
    [pipeline.byCompanyStatus],
  );

  const atRiskProjects = pipeline.atRiskProjects;
  const recentlyCompletedProjects = pipeline.recentlyCompletedProjects;

  const maxProductCount = Math.max(1, ...productsByCount.map((item) => item.count));
  const maxEngagementCount = Math.max(1, ...engagementByCount.map((item) => item.count));
  const maxRegionCount = Math.max(1, ...regionByCount.map((item) => item.count));
  const completedByRegionTotal = completedRegionQuarterRows.reduce(
    (sum, item) => sum + item.total,
    0,
  );
  const activeByRegionTotal = regionByCount.reduce(
    (sum, item) => sum + item.count,
    0,
  );
  const activeFyRate = metrics.totalProjects > 0
    ? Math.round((activeByRegionTotal / metrics.totalProjects) * 100)
    : 0;
  const completionRate = metrics.totalProjects > 0
    ? Math.round((completedByRegionTotal / metrics.totalProjects) * 100)
    : 0;

  const leadershipStatCards = useMemo(() => ([
    {
      kpi: LEADERSHIP_KPI_FILTERS.ALL,
      tone: 'total',
      value: metrics.totalProjects,
      label: 'Total registered',
      hint: 'All projects in pipeline',
      title: 'View all projects in PAE Queue',
    },
    {
      kpi: LEADERSHIP_KPI_FILTERS.REGISTERED_THIS_FY,
      tone: 'info',
      value: metrics.totalProjectsThisFy,
      label: 'Registered this FY',
      hint: 'FY27 registrations',
      title: 'View projects registered this FY in PAE Queue',
    },
    {
      kpi: LEADERSHIP_KPI_FILTERS.ACTIVE,
      tone: 'active',
      value: metrics.active,
      label: 'Active projects',
      hint: 'Currently in delivery',
      title: 'View active projects in PAE Queue',
    },
    {
      kpi: LEADERSHIP_KPI_FILTERS.AT_RISK,
      tone: 'at-risk',
      value: metrics.atRisk,
      label: 'At-risk projects',
      hint: 'Needs attention',
      title: 'View at-risk projects in PAE Queue',
    },
    {
      kpi: LEADERSHIP_KPI_FILTERS.COMPLETED_THIS_FY,
      tone: 'completed',
      value: metrics.completed,
      label: 'Completed this FY',
      hint: 'Closed this fiscal year',
      title: 'View completed projects this FY in PAE Queue',
    },
  ]), [metrics]);

  if (!access.canAccessLeadershipPages) {
    return <Navigate to={PARTNER_PORTAL_NOT_AUTHORIZED} replace />;
  }

  if (loading) {
    return (
      <>
        <Navbar activeTab="PARTNER_PORTAL" />
        <PartnerLayout access={access}>
          <section className={pageClassName}>
            <p className="partner-loading-text">Loading pipeline dashboard...</p>
          </section>
        </PartnerLayout>
      </>
    );
  }

  const handleProductDrilldown = (item) => {
    navigate(buildLeadershipPaeQueueUrl({
      dimension: 'product',
      value: item.product,
      kpi: LEADERSHIP_KPI_FILTERS.ACTIVE,
    }));
  };

  const handleEngagementDrilldown = (item) => {
    navigate(buildLeadershipPaeQueueUrl({
      dimension: 'engagement',
      value: item.engagementType,
      kpi: LEADERSHIP_KPI_FILTERS.ACTIVE,
    }));
  };

  const handleRegionDrilldown = (item) => {
    navigate(buildLeadershipPaeQueueUrl({
      dimension: 'region',
      value: item.region,
      kpi: LEADERSHIP_KPI_FILTERS.ACTIVE,
    }));
  };

  const handleCompletedRegionTotalDrilldown = ({ companyName }) => {
    navigate(buildLeadershipPaeQueueUrl({
      dimension: 'region',
      value: companyName,
      kpi: LEADERSHIP_KPI_FILTERS.COMPLETED_THIS_FY,
    }));
  };

  const handleCompletedRegionQuarterDrilldown = ({ companyName, status }) => {
    navigate(buildLeadershipPaeQueueUrl({
      dimension: 'region',
      value: companyName,
      kpi: LEADERSHIP_KPI_FILTERS.COMPLETED_THIS_FY,
      quarter: status,
    }));
  };

  const handleCompanyTotalDrilldown = ({ companyName }) => {
    navigate(buildLeadershipPaeQueueUrl({
      dimension: 'company',
      value: companyName,
    }));
  };

  const handleCompanyStatusDrilldown = ({ companyName, status }) => {
    navigate(buildLeadershipPaeQueueUrl({
      dimension: 'company',
      value: companyName,
      status,
    }));
  };

  const handleKpiCardClick = (kpi) => {
    navigate(buildLeadershipPaeQueueUrlFromKpi(kpi));
  };

  const handleKpiCardKeyDown = (event, kpi) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleKpiCardClick(kpi);
    }
  };

  const resolveLeadershipProjectId = (project) => (
    project?.id
    ?? project?.projectId
    ?? project?.project_id
    ?? project?._id
    ?? null
  );

  const handleOpenProjectInPaeQueue = (project) => {
    const projectId = resolveLeadershipProjectId(project);
    if (!projectId) {
      return;
    }
    navigate(buildPaeQueueProjectUrl(projectId));
  };

  const handleProjectListItemKeyDown = (event, project) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpenProjectInPaeQueue(project);
    }
  };

  const renderMetricList = (
    items,
    maxCount,
    getLabel,
    getColor,
    emptyMessage,
    scrollable = false,
    onCountClick = null,
  ) => (
    <div className={`leadership-metric-list ${scrollable ? 'leadership-metric-list--scroll' : ''}`}>
      {items.length === 0 ? (
        <p className="leadership-empty">{emptyMessage}</p>
      ) : (
        items.map((item, index) => (
          <LeadershipMetricRow
            key={getLabel(item, index)}
            label={getLabel(item, index)}
            count={item.count}
            maxCount={maxCount}
            color={getColor(item, index)}
            onCountClick={onCountClick ? () => onCountClick(item) : null}
          />
        ))
      )}
    </div>
  );

  const renderProjectList = (
    projects,
    emptyMessage,
    badgeLabel,
    badgeClassName = '',
    { showProjectStatus = false } = {},
  ) => (
    <div className="leadership-risk-list leadership-risk-list--scroll">
      {projects.length === 0 ? (
        <p className="leadership-empty">{emptyMessage}</p>
      ) : (
        projects.map((project) => (
          <article
            className="leadership-risk-item leadership-risk-item--clickable"
            key={project.id}
            role="button"
            tabIndex={0}
            title="Open project in PAE Queue"
            onClick={() => handleOpenProjectInPaeQueue(project)}
            onKeyDown={(event) => handleProjectListItemKeyDown(event, project)}
          >
            <div className="leadership-risk-copy">
              <h6>{project.customerName} ({project.partnerName})</h6>
              <p>{(project.productsInScope || []).join(' · ') || project.engagementType || '-'}</p>
            </div>
            <div className="leadership-risk-meta">
              <span>{formatPartnerDate(project.estimatedGoliveDate)}</span>
              <b className={badgeClassName}>
                {showProjectStatus ? (project.projectStatus || badgeLabel) : badgeLabel}
              </b>
            </div>
          </article>
        ))
      )}
    </div>
  );

  return (
    <>
      <Navbar activeTab="PARTNER_PORTAL" />
      <PartnerLayout access={access}>
        <section className={pageClassName}>
          <PageHeader
            title="Pipeline Dashboard"
            description="Partner delivery activity across all regions and products · FY27"
            rightContent={(
              <>
                {darkModeControl}
                <span className="leadership-view-pill">Leadership View · Global</span>
              </>
            )}
          />

          {error ? <p className="text-danger">{error}</p> : null}

          <div className="leadership-scroll-card">
            <div className="partner-leadership-stats-row">
              {leadershipStatCards.map((card) => (
                <button
                  key={card.kpi}
                  type="button"
                  className={`partner-portal-stat-card partner-portal-stat-card--${card.tone}`}
                  title={card.title}
                  onClick={() => handleKpiCardClick(card.kpi)}
                  onKeyDown={(event) => handleKpiCardKeyDown(event, card.kpi)}
                >
                  <span className="partner-portal-stat-card-accent" aria-hidden="true" />
                  <span className="partner-portal-stat-card-body">
                    <span className="partner-portal-stat-card-top">
                      <span className="partner-portal-stat-card-value">{card.value}</span>
                      <span className="partner-portal-stat-card-label">{card.label}</span>
                    </span>
                    <span className="partner-portal-stat-card-hint">{card.hint}</span>
                  </span>
                </button>
              ))}
            </div>

            <div className="leadership-panels-grid">
              <section className="leadership-panel leadership-panel--equal">
                <LeadershipMetricPanelHeader
                  tone="product"
                  eyebrow="Active projects"
                  title="By product"
                  subtitle="All projects with Active status"
                />
                <div className="leadership-panel-body">
                  {renderMetricList(
                    productsByCount,
                    maxProductCount,
                    (item) => item.product,
                    (_item, index) => PRODUCT_COLORS[index % PRODUCT_COLORS.length],
                    'No active projects by product.',
                    true,
                    handleProductDrilldown,
                  )}
                </div>
              </section>

              <section className="leadership-panel leadership-panel--equal">
                <LeadershipMetricPanelHeader
                  tone="engagement"
                  eyebrow="Active projects"
                  title="By engagement type"
                  subtitle="All projects with Active status"
                />
                <div className="leadership-panel-body">
                  {renderMetricList(
                    engagementByCount,
                    maxEngagementCount,
                    (item) => item.engagementType,
                    (_item, index) => ENGAGEMENT_COLORS[index % ENGAGEMENT_COLORS.length],
                    'No active projects by engagement type.',
                    true,
                    handleEngagementDrilldown,
                  )}
                </div>
              </section>

              <section className="leadership-panel leadership-panel--equal">
                <LeadershipMetricPanelHeader
                  tone="active-region"
                  eyebrow="Active projects"
                  title="By region"
                  subtitle="All active projects"
                />
                <div className="leadership-panel-body leadership-panel-body--compact">
                  {renderMetricList(
                    regionByCount,
                    maxRegionCount,
                    (item) => REGION_LABELS[item.region] || item.region,
                    () => '#1174e6',
                    'No active projects by region.',
                    true,
                    handleRegionDrilldown,
                  )}
                  <div className="leadership-completion-strip leadership-completion-strip--fy-active">
                    <p>Active rate</p>
                    <div className="leadership-completion-value">
                      <strong>{activeFyRate}%</strong>
                      <span>
                        {activeByRegionTotal} active by region vs. {metrics.totalProjects} total registered
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              <section className="leadership-panel leadership-panel--full">
                <LeadershipMetricPanelHeader
                  tone="completed-region"
                  eyebrow="Completed projects"
                  title="By region"
                  subtitle="Completed this FY · stacked by BMC quarter (Q1 Apr–Jun)"
                />
                <div className="leadership-panel-body leadership-panel-body--compact">
                  <LeadershipCompanyStackedChart
                    companies={completedRegionQuarterRows}
                    legend={pipeline.regionQuarterLegend}
                    emptyMessage="No completed projects by region."
                    onCompanyTotalClick={handleCompletedRegionTotalDrilldown}
                    onCompanyStatusClick={handleCompletedRegionQuarterDrilldown}
                  />
                  <div className="leadership-completion-strip leadership-completion-strip--fy-completion">
                    <p>FY completion rate</p>
                    <div className="leadership-completion-value">
                      <strong>{completionRate}%</strong>
                      <span>
                        {completedByRegionTotal} completed by region vs. {metrics.totalProjects} total registered
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              <section className="leadership-panel leadership-panel--full">
                <header>
                  <h4>Projects by company</h4>
                  <span className="leadership-panel-badge">By status</span>
                </header>
                <div className="leadership-panel-body">
                  <LeadershipCompanyStackedChart
                    companies={companyStatusRows}
                    legend={pipeline.companyStatusLegend}
                    emptyMessage="No company data available."
                    onCompanyTotalClick={handleCompanyTotalDrilldown}
                    onCompanyStatusClick={handleCompanyStatusDrilldown}
                  />
                </div>
              </section>

              <div className="leadership-panels-split">
                <section className="leadership-panel leadership-panel--split">
                  <header>
                    <h4 className="is-warning">At-risk projects</h4>
                    <span className="leadership-panel-badge is-warning">Needs attention</span>
                  </header>
                  <div className="leadership-panel-body">
                    {renderProjectList(
                      atRiskProjects,
                      'No at-risk projects right now.',
                      'At Risk',
                    )}
                  </div>
                </section>

                <section className="leadership-panel leadership-panel--split">
                  <header>
                    <h4 className="is-success">Completed in last 30 days</h4>
                    <span className="leadership-panel-badge is-success">Recent closures</span>
                  </header>
                  <div className="leadership-panel-body">
                    {renderProjectList(
                      recentlyCompletedProjects,
                      'No projects completed in the last 30 days.',
                      'Completed',
                      'is-success',
                      { showProjectStatus: true },
                    )}
                  </div>
                </section>
              </div>
            </div>
          </div>
        </section>
      </PartnerLayout>
    </>
  );
};

export default LeadershipDashboardPage;
